import React, {Fragment} from 'react';
import Box from '../layout/Box';
import createEditor, {referenceActionData} from '../form/ComponentEditor';
import BoxEditor from "./BoxEditor";
import Button from "../form/Button";
import NewsLetterList from "./lists/NewsLetterList";
import NewsletterForm from "./forms/NewsletterForm";
import {postData, sendRequest} from "../../utils/request";
import NewsletterTargetEditor from "./NewsletterTargetEditor";
import ModalEditor from "./ModalEditor";
import Modal from "../layout/Modal";
import isEqual from "lodash-es/isEqual";
import {loadComponent, loadComponentById} from "../../utils/components";
import ContentLoader from "../layout/ContentLoader";
import {delay} from "../../utils/other";
import Tabs from "../layout/Tabs";
import TabContent from "../layout/TabContent";
import Tab from "../layout/Tab";
import classNames from "classnames";
import Icon from "../form/Icon";
import FragmentEditor from "./FragmentEditor";
import {ScheduledTaskStatus} from "./ScheduledTaskEditor";

const NewsletterEditorTab = (props) => {
    if (!(props.newsletters || []).length) {
        return <BoxEditor {...props} footer={false} buttons={false}>
            <ContentLoader />
        </BoxEditor>;
    }
    return <Tabs
        theme="default"
        className="tab-box newsletter-tabs"
        currentTab={(props.values.id || "new") + ""}
        onTabChange={props.onTabChange}
        tab={(tabProps) => {
            return <Tab {...tabProps} className={classNames({current: tabProps.tabName + "" === "" + props.parentId, "pull-right": tabProps.tabName === "new"})}>{tabProps.tabLabel}</Tab>;
        }}
    >
        {(props.newsletters || []).map((newsletter, key) => {
            return <TabContent
                key={key}
                tabLabel={<Fragment>{(newsletter.name || "") + " (#" + newsletter.id + ")"}{newsletter.id === props.values.id && (props || {}).dirty && <small className={"label pull-right bg-red"}>!</small>}</Fragment>}
                tabName={newsletter.id + ""}
            >
                <FragmentEditor {...props} />
            </TabContent>;
        })}
        {props.parentId && <TabContent
            tabLabel={<Icon lib="fas" name="folder-plus" tooltip="Új hírlevél létrehozása és hozzáadás ehhez a csoporthoz" />}
            tabName="new"
        >
           <FragmentEditor {...props} />
        </TabContent>}
    </Tabs>;
}

class NewsletterEditor extends React.Component {

    state = {
        action: null,
        sendTo: null,
        sending: 0,
        suspended: 0,
        newsletters: [],
        isLoading: true
    }

    updateNewsletterList = () => {
        this.setState({isLoading: true}, () => {
            this.loadNewsletters && this.loadNewsletters.abort();
            const id = (this.props.values.parent instanceof Object ? (this.props.values.parent || {})[this.props.componentConfig.idfield] : this.props.values.parent) || this.props.values[this.props.componentConfig.idfield] || null;
            if (id) {
                this.loadNewsletters = loadComponent("componentlist", {
                    component: "Newsletter",
                    itemsperpage: 0,
                    view: [this.props.componentConfig.idfield, "name"],
                    filter: {filters: [{field: "parent", value: id},{field: this.props.componentConfig.idfield, value: id}], operator: "or"}
                });
                this.loadNewsletters.then((all) => {
                    this.setState({
                        newsletters: all.items,
                        parentId: id,
                        isLoading: false
                    });
                });
            } else {
                this.setState({
                    newsletters: [],
                    parentId: null,
                    isLoading: false
                });
            }
        });
    }

    componentDidUpdate(prevProps, prevState, prevContext) {
        if (!isEqual(prevProps.values[this.props.componentConfig.idfield], this.props.values[this.props.componentConfig.idfield]) || !isEqual(this.props.values.parent, prevProps.values.parent)) {
            this.setState({
                sending: parseInt(this.props.values.sending || "0"),
                suspended: parseInt(this.props.values.suspended || "0"),
            });
            this.componentDidMount();
        } else if (this.state.action === "send" && prevState.action !== this.state.action) {
            this.loadRecipients();
        }
    }

    loadRecipients = () => {
        this.setState({sendTo: null}, () => {
            this.targets && this.targets.abort();
            if (this.props.values[this.props.componentConfig.idfield]) {
                this.targets = postData({
                    action: this.props.componentConfig.componentname + ".recipientCount",
                    'target_params': this.props.values.target_params,
                    [this.props.componentConfig.idfield]: this.props.values[this.props.componentConfig.idfield]
                });
                this.targets.then((response) => {
                    this.setState({
                        sendTo: response.count
                    });
                });
            } else {
                this.setState({
                    sendTo: null
                });
            }
        });
    }

    constructor(props) {
        super(props);
        this.focused = document.hasFocus();
    }

    onFocus = () => {
        this.focused = true;
    }

    onBlur = () => {
        this.focused = false;
    }

    componentDidMount() {
        window.addEventListener("focus", this.onFocus);
        window.addEventListener("blur", this.onBlur);
        this.loadRecipients();
        this.updateNewsletterList();
        if (!this.refreshCount) {
            this.refreshCount = async () => {
                if (this.props.values[this.props.componentConfig.idfield]) {
                    if (this.focused) {
                        loadComponentById(this.props.componentConfig.componentname, this.props.values[this.props.componentConfig.idfield], ["suspended", "sending"]).then(async (data) => {
                            if (parseInt(data.sending || "0") !== this.state.sending || parseInt(data.suspended || "0") !== this.state.suspended) {
                                this.setState({
                                    sending: parseInt(data.sending || "0"),
                                    suspended: parseInt(data.suspended || "0"),
                                }, async () => {
                                    await delay(5000);
                                    this.refreshCount && this.refreshCount();
                                });
                            } else {
                                await delay(5000);
                                this.refreshCount && this.refreshCount();
                            }
                        });
                    } else {
                        await delay(5000);
                        this.refreshCount && this.refreshCount();
                    }
                } else {
                    if (parseInt(this.state.sending || "0") !== 0 || parseInt(this.state.suspended || "0") !== 0) {
                        this.setState({
                            sending: 0,
                            suspended: 0,
                        }, async () => {
                            await delay(5000);
                            this.refreshCount && this.refreshCount();
                        });
                    } else {
                        await delay(5000);
                        this.refreshCount && this.refreshCount();
                    }
                }
            };
            this.refreshCount();
        }
    }

    componentWillUnmount() {
        window.removeEventListener("focus", this.onFocus);
        window.removeEventListener("blur", this.onBlur);
        this.refreshCount = null;
    }

    loadForm = async (data) => {
        await this.props.loadForm(data);
        this.editor && this.editor.focus();
        this.loadRecipients();
    }

    pristineForm = async () => {
        await this.props.pristineForm();
        this.editor && this.editor.focus();
        this.loadRecipients();
    }

    pristineParentForm = async () => {
        let values = {
            parent: this.state.parentId,
            recipient_params: {
                filter: {
                    filters: [],
                    operator: "and"
                }
            }
        };
        await this.props.pristineForm();
        await this.props.setInitialValues(values);
        await this.props.setFormValues(values);
        this.editor && this.editor.focus();
        this.loadRecipients();
    }

    saveForm = async (event) => {
        await this.props.saveForm(event);
        this.list && this.list.refresh();
        this.loadRecipients();
    }

    sendAction = async () => {
        if (this.state.action === "send" && this.state.sendTo === null) return;
        await this.props.processAction(async () => {
            return await postData({
                action: 'Newsletter.' + this.state.action,
                data: this.props.values,
                view: this.props.view,
            });
        });
    }

    cloneNewsletter = async (parent) => {
        await this.props.processAction(async () => {
            const res = await postData({
                action: 'Newsletter.cloneNewsletter',
                id: this.props.values[this.props.componentConfig.idfield],
                parent
            });
            if (this.props.cloneNewsletter) {
                this.props.cloneNewsletter(res.data);
            } else {
                this.loadForm(res.data);
            }
            return res;
        });
    }

    render() {
        const List = this.props.list || Box;
        let Editor = this.props.editor;
        if (Editor === undefined) {
            if (this.state.parentId || this.props.values[this.props.componentConfig.idfield] || this.props.values.parent) {
                Editor = NewsletterEditorTab;
            } else {
                Editor = BoxEditor
            }
        }
        const Other = this.props.other || Fragment;
        const suspendedCount = parseFloat(this.state.suspended || "0");
        const suspended = suspendedCount > 0;
        const sendingCount = parseFloat(this.state.sending || "0");
        const sending = sendingCount > 0 || this.props.values.state + "" === "5";
        const disabled = suspended || sending;

        const id = this.state.parentId || this.props.values[this.props.componentConfig.idfield];

        const actionButtons = [
            <Button
                key="0"
                disabled={!this.props.dirty || this.props.isLoading || disabled}
                color="primary"
                className="pull-right"
                onClick={this.saveForm}
            >
                Mentés
            </Button>,
            <Button
                key="9"
                disabled={!this.props.values[this.props.componentConfig.idfield] || this.props.dirty || this.props.isLoading}
                color="default"
                className="pull-right"
                icon={{lib: "fas", name: "folder-plus"}}
                tooltip="Új hírlevél ebből a hírlevélből és adja hozzá ehhez a csoporthoz"
                onClick={() => this.cloneNewsletter(undefined)}
            >
                Másolás
            </Button>,
            <Button
                key="10"
                disabled={!this.props.values[this.props.componentConfig.idfield] || this.props.dirty || this.props.isLoading}
                color="default"
                className="pull-right"
                icon={{lib: "fas", name: "plus"}}
                tooltip="Új hírlevél ebből a hírlevélből"
                onClick={() => this.cloneNewsletter(null)}
            >
                Másolás
            </Button>,
            <Button
                key="11"
                disabled={!this.props.values[this.props.componentConfig.idfield] || disabled}
                color="danger"
                className="pull-left"
                onClick={this.props.deleteForm}
            >
                Törlés
            </Button>
        ];

        if (disabled) {
            actionButtons.push(
                <Button
                    key="2"
                    icon={{lib: "fas", name: "stop"}}
                    disabled={
                        this.props.dirty
                        ||
                        !this.props.values[this.props.componentConfig.idfield]
                        ||
                        this.props.isLoading
                    }
                    color="danger"
                    onClick={() => this.setState({action: 'stop'})}
                    className="pull-right"
                >
                    Leállítás ({(this.state.sending + this.state.suspended) || this.state.sendTo || 0} db)
                </Button>
            );
        }

        if (sending && this.state.sending > 0) {
            actionButtons.push(
                <Button
                    key="3"
                    icon={{lib: "fas", name: "pause"}}
                    disabled={
                        this.props.dirty
                        ||
                        !this.props.values[this.props.componentConfig.idfield]
                        ||
                        this.props.isLoading
                        ||
                        !sending
                    }
                    color="warning"
                    onClick={() => this.setState({action: 'suspend'})}
                    className="pull-right"
                >
                    Szüneteltetés ({this.state.sending} db)
                </Button>
            );
        } else if (suspended || !sending) {
            actionButtons.push(
                <Button
                    key="1"
                    icon={{lib: "fa", name: "paper-plane"}}
                    disabled={
                        this.props.dirty
                        ||
                        !this.props.values[this.props.componentConfig.idfield]
                        ||
                        this.props.isLoading
                        ||
                        !(suspended || !sending)
                    }
                    color="default"
                    onClick={() => this.setState({action: 'send'})}
                    className="pull-right"
                >
                    {suspended ? "Szüneteltetve" : "Küldés"} ({suspended ? this.state.suspended : (this.state.sendTo || 0 || sendingCount)} db)
                </Button>
            );
        }

        return <Fragment>
            {this.state.action && <Modal
                header="Biztos?"
                state="on"
                size={"small"}
                buttons={[
                    {
                        text: "Mégse",
                        props: {color: "default", disabled: this.props.disabled || this.props.isLoading},
                        onClose: true,
                        onClick: async () => {
                            try {
                                this.setState({action: null});
                            } catch (e) {

                            }
                        }
                    },
                    {
                        text: "Igen",
                        props: {color: this.state.action === "stop" ? "danger" : (this.state.action === "send" ? "primary" : "warning"), disabled: (this.state.sendTo === null && this.state.action === "send") || this.props.disabled || this.props.isLoading},
                        onClick: async () => {
                            try {
                                await this.sendAction();
                                this.setState({action: null});
                            } catch (e) {

                            }
                        }
                    }
                ]}
            >
                {this.state.action === "send" ? (this.state.sendTo !== null ? <p><b>{this.state.sendTo} db hírlevél fog kimenni!</b></p> : <ContentLoader/>) : null}
                <p>{this.state.action === "stop" ? "Biztos leállítod?" : (this.state.action === "send" ? "Biztos elindítod?" : "Biztos szünetelteted?")}</p>
            </Modal>}
            <ScheduledTaskStatus tasks={[1]} header="Levél küldés" />
            <List
                {...this.props}
                theme="default"
                header="Hírlevelek"
                footer={[
                    <Button key="0" disabled={this.props.isLoading} color="default" onClick={this.pristineForm}>
                        Új
                    </Button>
                ]}
            >
                <NewsLetterList ref={(el) => this.list = el} {...this.props} loadForm={this.loadForm} selection={{[this.props.componentConfig.idfield]: id}} />
            </List>
            <Editor
                {...this.props}
                saveForm={this.saveForm}
                newsletters={this.state.newsletters}
                parentId={this.state.parentId}
                onTabChange={async (id) => {
                    if (id !== "new") {
                        await this.loadForm({id});
                    } else {
                        await this.pristineParentForm();
                    }
                }}
                ref={(editor) => this.editor = editor}
                theme="default"
                header="Hírlevél"
                footer={actionButtons}
            >
                <NewsletterForm
                    {...this.props}
                    disabled={disabled}
                    newsletters={this.state.newsletters}
                    saveForm={this.saveForm}
                />
            </Editor>
            <Other>
            {
                id &&
                this.props.componentConfig.fielddefs.targets &&
                <NewsletterTargetEditor
                    newsletterDisabled={disabled}
                    editorContext={"NewsletterNewsletterTargetList"}
                    gridid={"NewsletterNewsletterTargetList"}
                    presets={{newsletter: id}}
                    parentId={id}
                    newsletterEditors={this.props.newsletterEditors}
                    isLoading={this.props.isLoading}
                    editor={ModalEditor}
                    selection={null}
                    listFilter={[
                        {
                            field: "newsletter",
                            value: id
                        }
                    ]}
                />
            }
            </Other>
        </Fragment>;
    }
}

export default createEditor({
    componentName: "Newsletter",
    WrappedComponent: NewsletterEditor,
    props: {
        view: "admin",
        componentActions: async (action, data, params, componentConfig) => {
            data = await referenceActionData(data, componentConfig);
            data.campaign = (data.campaign || {}).id;
            return {action,data};
        }
    }
});